<template>
  <v-layout row wrap class="card-homes-container">
    <template v-if="favorites && favorites.length > 0">
      <modal-send-message
        :show-modal="displayMessageModal"
        :selected-property="selectedProperty"
        @close-modal="displayMessageModal = false"
        @submit-modal="sendMessage($event)"
      />
      <v-flex
        v-for="(favorite, index) in favorites"
        :key="index"
        xs12
        sm6
        lg4
        class="fav-card-wrapper"
      >
        <!-- The event handlers should probably be separate API calls,
        but at this point I am just propagating them to the Saved Favorites view.-->
        <card-home
          ref="cardHome"
          :property-info="favorite"
          :index="index"
          :home-type="'favorite'"
          :property-type="propertyType"
          :is-tour-requested="isTourRequested(favorite)"
          @card-un-set-favorite="unSetFavorite(favorite)"
          @card-request-tour="requestTour(favorite)"
          @card-view-detail="showFavoriteDetails(favorite, $event)"
        >
          <template v-slot:hoverCard="props">
            <v-flex xs12>
              <div
                class="card-favorite__overlay-link"
                @click.stop="showFavoriteDetails(favorite, $event)"
              >
                <v-icon color="white" class="result-overlay-icon">
                  fas fa-search
                </v-icon>View Details
              </div>
            </v-flex>
            <v-flex
              v-if="propertyType === 'favorites' && !isTourRequested(favorite)"
              xs12
            >
              <div
                class="card-favorite__overlay-link"
                @click.stop="unSetFavorite(favorite)"
              >
                <v-icon color="red" class="result-overlay-icon">
                  fas fa-heart
                </v-icon>Unfavorite
              </div>
            </v-flex>
            <v-flex v-if="displayRequestTour(favorite)" xs12>
              <div
                class="card-favorite__overlay-link"
                :class="{'non-active-click': isTourRequested(favorite)}"
                @click.stop="
                  isTourRequested(favorite) ? null : requestTour(favorite)"
              >
                <v-icon
                  color="white"
                  class="result-overlay-icon"
                  :class="{ 'green--text': isTourRequested(favorite) }"
                >
                  fas fa-paper-plane
                </v-icon>
                {{ getRequestTourValue(favorite) }}
              </div>
            </v-flex>
            <v-flex xs12>
              <div
                class="card-favorite__overlay-link"
                @click.stop="openMessageModal(favorite)"
              >
                <v-icon color="white" class="result-overlay-icon">
                  fas fa-envelope
                </v-icon>Send Message
              </div>
            </v-flex>
            <template v-if="canRequest(favorite)">
              <v-flex v-if="favorite.PropertyType === 'Residential'" xs12>
                <modal-make-offer
                  v-show="$refs.cardHome[index].modalShowing"
                  ref="modalMakeOffer"
                  :home="favorite"
                  :agent="
                    $refs.cardHome[index].associatedAgent
                      ? $refs.cardHome[index].associatedAgent
                      : ''"
                  @submit-modal="sendOfferMessage($event)"
                  @modal-changed="
                    $refs.cardHome[index].modalShowing =
                      $event === false
                        ? false
                        : $refs.cardHome[index].modalShowing"
                />
                <div
                  :class="[
                    'card-favorite__overlay-link',
                    { 'non-active-click': isOffered(favorite) }
                  ]"
                  @click.stop="
                    isOffered(favorite) ? null : setModalShowing(index, true)"
                >
                  <v-icon
                    :color="
                      isOffered(favorite) ? colors.pending : colors.hwayzGray"
                    class="result-overlay-icon"
                  >
                    fas fa-arrow-alt-circle-right
                  </v-icon>
                  {{ isOffered(favorite) ? "Offer Sent" : "Make Offer" }}
                </div>
              </v-flex>
              <v-flex v-else xs12>
                <modal-rent-this
                  v-show="$refs.cardHome[index].modalShowing"
                  ref="modalRentThis"
                  :home="favorite"
                  :agent="
                    $refs.cardHome[index].associatedAgent
                      ? $refs.cardHome[index].associatedAgent
                      : ''"
                  @modal-changed="
                    $refs.cardHome[index].modalShowing =
                      $event === false
                        ? false
                        : $refs.cardHome[index].modalShowing"
                  @submit-modal="sendRentMessage($event)"
                />
                <div
                  class="card-favorite__overlay-link"
                  @click.stop="setModalRentShowing(index, true)"
                >
                  <v-icon color="white" class="result-overlay-icon">
                    fas fa-arrow-alt-circle-right
                  </v-icon>Rent this
                </div>
              </v-flex>
            </template>
          </template>
        </card-home>
      </v-flex>
    </template>
    <div v-else class="m-auto">
      <title-with-message
        title="No Toured favorites yet"
        message="When you have viewed a scheduled route property, you can check their details from here"
        :content-small="true"
      />
    </div>
  </v-layout>
</template>

<script>
import { mapState, mapActions, mapMutations } from 'vuex'
import CardHome from '../../uiUtils/CardHome.vue'
import ModalMakeOffer from '../modals/ModalMakeOffer.vue'
import ModalRentThis from '../modals/ModalRentThis.vue'
import ModalSendMessage from '../modals/ModalSendMessage.vue'
import TitleWithMessage from '../../uiUtils/TitleWithMessage.vue'
import {
  sendMessageAboutHome,
  sendOfferMessage,
  sentRentThisMessage,
  setOfferTimestamp
} from '../../../services/messagesService'
import {
  requestPropertyTour,
  removeFromClientFavorite
} from '../../../services/propertiesService'
import { isOfferMade } from '../../../utils/propertyUtils'
import { HIDE_STATUSES } from '../../common/searchPropertyConst'
import { activeRouteHomeIds } from '../../../services/routeService'
import colors from '../../../scss/_colors.scss'

export default {
  components: {
    ModalMakeOffer,
    CardHome,
    ModalSendMessage,
    TitleWithMessage,
    ModalRentThis
  },

  props: {
    favorites: {
      type: Array,
      required: true
    },
    propertyType: {
      type: String,
      required: true
    }
  },

  data() {
    return {
      displayMessageModal: false,
      selectedProperty: null,
      active: 0,
      colors,
      activeRouteHomeIds: []
    }
  },

  computed: {
    ...mapState('client', ['associatedAgent', 'clientData', 'clientFavorites']),
    ...mapState('common', ['yScrollPosition', 'selectedFavoritesTab', 'doScroll'])
  },

  watch: {
    clientData: {
      handler: function(value) {
        this.activeRouteHomeIds = activeRouteHomeIds(value)
      },
      deep: true
    }
  },

  mounted() {
    this.activeRouteHomeIds = activeRouteHomeIds(this.clientData)
    if (this.favorites.length) {
      if (this.doScroll) {
        setTimeout(() => {
          window.scrollTo(0, this.yScrollPosition)
        }, 500)
      }
    }
  },

  destroyed() {
    this.setDoScroll(false)
  },

  methods: {
    ...mapActions('client', ['updateClientPropertiesAction']),
    ...mapActions('common', ['setSelectedFavoritesTab']),
    ...mapMutations('common', {
      setYScrollPosition: 'SET_Y_SCROLL_POSITION',
      setDoScroll: 'SET_DO_SCROLL'
    }),

    setActive(val) {
      this.active = val
      this.$emit('tab-changed', val)
    },

    isOffered(property) {
      return isOfferMade(property)
    },

    canRequest(fav) {
      return !HIDE_STATUSES.includes(fav.Status)
    },

    displayRequestTour(data) {
      // if (this.propertyType === 'toured' && !this.isTourRequested(data)) {
      //   return false
      // }
      return this.canRequest(data) || this.isTourRequested(data)
    },

    isTourRequested(fav) {
      return fav.requestTour || this.activeRouteHomeIds.includes(fav._id)
    },

    async unSetFavorite(item) {
      if (!this.isTourRequested(item)) {
        const updatedClientProperties = await removeFromClientFavorite(
          this.clientData,
          this.clientFavorites,
          item
        )
        if (updatedClientProperties) {
          this.updateClientPropertiesAction(updatedClientProperties)
        }
      }
    },

    getRequestTourValue(data) {
      if (this.propertyType === 'toured') {
        return this.isTourRequested(data) ? 'Tour Requested' : 'Request Again'
      } else {
        return this.isTourRequested(data) ? 'Tour Requested' : 'Request Tour'
      }
    },

    async requestTour(data) {
      const updatedClientProperties = await requestPropertyTour(
        this.clientData,
        this.clientFavorites,
        data
      )
      if (updatedClientProperties) {
        this.updateClientPropertiesAction(updatedClientProperties)
      }
    },

    showFavoriteDetails(favorite, event) {
      this.saveScrollPosition(event)
      this.$router.push({
        path: `favorites/${favorite._id}`
      })
    },
    saveScrollPosition(event) {
      this.propertyType !== 'toured' ? this.setSelectedFavoritesTab(0) : this.setSelectedFavoritesTab(1)
      const pathIndex = event.path.findIndex(item => item.className.includes('home-card'))
      this.setYScrollPosition(event.path[pathIndex].offsetTop - 48)
    },
    setModalShowing(i, val) {
      // i - there are more than one component with ref modalMakeOffer, so 'i' helps us find the right one
      this.$refs.cardHome[i].modalShowing = val
      this.$refs.modalMakeOffer[0].setModalShowing(val)
    },
    setModalRentShowing(i, val) {
      this.$refs.cardHome[i].modalShowing = val
      this.$refs.modalRentThis[0].setModalRentShowing(val)
    },

    async sendMessage(data) {
      await sendMessageAboutHome(data)
      this.displayMessageModal = false
    },

    async sendRentMessage(data) {
      await sentRentThisMessage(data)
    },

    openMessageModal(home) {
      this.displayMessageModal = true
      this.selectedProperty = home
    },

    async sendOfferMessage(data) {
      await sendOfferMessage(data)
      const response = await setOfferTimestamp(
        this.clientData._id,
        data.favoriteId
      )
      this.$emit('offer-message-sent', response.data)
    }
  }
}
</script>

<style lang="sass" scoped>
.result-overlay-icon
  @extend %card__hover-icon
  width: 3.2rem

.card-favorite__overlay-link
  @extend %card__hover-link
  margin: 1.3rem 0.5rem
  &:hover
    color: $hwayzBrandGold

.non-active-click
  @extend %disable-selection
  cursor: default
  &:hover
    color: $hwayzWhite

.fav-card-wrapper
  padding: 0 8px
  margin-bottom: 1rem
  @media only screen and (min-width: 600px)
    margin-bottom: 1.5rem

.card-homes-container
  margin-top: 2rem
  @media only screen and (max-width: 600px)
    margin-top: 35px
</style>
